import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { cn } from '@/shared/lib/utils';

/** Tempo de cada slide antes de passar sozinho. */
const AUTOPLAY_MS = 4500;

interface StoreCoverProps {
  /** URL da capa personalizada da loja (gestor); vazio = só o banner da pratiGO. */
  coverUrl?: string | null;
  grayscale?: boolean;
}

/**
 * Banner do topo da loja (PRAT-154): um slide que passa sozinho e com o dedo.
 *
 * - O banner da pratiGO é fixo no código e sempre aparece (a loja não remove).
 * - Se a loja cadastrou a capa no gestor, ela é o primeiro slide.
 * - Cada imagem aparece inteira (`contain`), com a própria imagem desfocada no
 *   fundo — capa de qualquer tamanho não é cortada. A faixa de baixo (`pb-6`)
 *   fica por trás do cartão branco da loja.
 */
export function StoreCover({ coverUrl, grayscale }: StoreCoverProps) {
  const slides: Array<'cover' | 'pratigo'> = coverUrl ? ['cover', 'pratigo'] : ['pratigo'];
  const [coverFailed, setCoverFailed] = useState(false);
  const visible = coverFailed ? slides.filter(s => s !== 'cover') : slides;

  const [emblaRef, embla] = useEmblaCarousel({ loop: true, active: visible.length > 1 });
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelected(embla.selectedScrollSnap());
    embla.on('select', onSelect);
    embla.on('reInit', onSelect);
    return () => {
      embla.off('select', onSelect);
      embla.off('reInit', onSelect);
    };
  }, [embla]);

  // Passa sozinho; o toque do cliente reinicia a contagem.
  const [pausedAt, setPausedAt] = useState(0);
  useEffect(() => {
    if (!embla || visible.length < 2) return;
    const id = setInterval(() => embla.scrollNext(), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [embla, visible.length, pausedAt]);
  const onPointerDown = useCallback(() => setPausedAt(Date.now()), []);

  return (
    <div
      className="relative w-full flex-shrink-0 overflow-hidden bg-gray-100 pb-6"
      aria-roledescription="carrossel"
    >
      <div ref={emblaRef} className="overflow-hidden" onPointerDown={onPointerDown}>
        <div className="flex">
          {visible.map((slide, i) => (
            <div
              key={slide}
              className="relative min-w-0 shrink-0 grow-0 basis-full"
              aria-roledescription="slide"
              aria-label={`${i + 1} de ${visible.length}`}
            >
              <div
                className="relative w-full max-h-80 [container-type:inline-size]"
                style={{ aspectRatio: 2 }}
              >
                {slide === 'cover' ? (
                  <>
                    <img
                      src={coverUrl!}
                      alt=""
                      aria-hidden
                      className={cn(
                        'absolute inset-0 h-full w-full scale-110 object-cover opacity-70 blur-xl',
                        grayscale && 'grayscale'
                      )}
                    />
                    <img
                      src={coverUrl!}
                      alt="Capa da loja"
                      fetchPriority="high"
                      decoding="async"
                      className={cn(
                        'relative h-full w-full object-contain',
                        grayscale && 'grayscale'
                      )}
                      onError={() => setCoverFailed(true)}
                    />
                  </>
                ) : (
                  <PratigoBanner grayscale={grayscale} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {visible.length > 1 && (
        <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-1.5" aria-hidden>
          {visible.map((slide, i) => (
            <span
              key={slide}
              className={cn(
                'h-1.5 rounded-full bg-white/60 shadow transition-all',
                i === selected ? 'w-5 bg-white' : 'w-1.5'
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Banner fixo da pratiGO (desenhado no código, nítido em qualquer tela). */
function PratigoBanner({ grayscale }: { grayscale?: boolean }) {
  return (
    <div
      role="img"
      aria-label="Este estabelecimento é parceiro pratiGO"
      className={cn(
        'absolute inset-0 flex flex-col items-center justify-center gap-[min(2.8cqw,14px)] pb-[min(7cqw,32px)] bg-gradient-to-br from-[#ff8004] to-[#ff5e00] px-6 text-center text-white',
        grayscale && 'grayscale'
      )}
    >
      <p className="text-[min(5.2cqw,26px)] font-bold leading-tight whitespace-nowrap">
        Este estabelecimento é parceiro
      </p>
      <span className="rounded-2xl bg-[#1a1a1a] px-[min(4cqw,20px)] py-[min(2.5cqw,12px)] shadow-lg">
        <img src="/logo-dark.svg" alt="pratiGO" className="h-[min(6cqw,30px)] w-auto" />
      </span>
      <p className="text-[min(3cqw,14px)] font-semibold tracking-wide opacity-95">
        PDV · DELIVERY · MESAS · FISCAL SIMPLES
      </p>
      <p className="text-[min(2.8cqw,13px)] opacity-90">@pratigobrasil · www.pratigo.com.br</p>
    </div>
  );
}
